import React, { useEffect, useState } from "react";
import { Card, Col, Container, Form, Row, Table, Button } from "react-bootstrap";
import axiosInstance from "../../utils/axiosUtil";
import LoadingBox from "../layout/LoadingBox";
import MessageBox from "../layout/MessageBox";

export default function Industries() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const loadIndustries = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axiosInstance.get("/industries");
      setIndustries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load industries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIndustries();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setStatus("Industry name is required");
      return;
    }
    setSaving(true);
    setStatus("");
    try {
      await axiosInstance.post("/industries", {
        name: form.name.trim(),
        description: form.description.trim(),
      });
      setForm({ name: "", description: "" });
      await loadIndustries();
      setStatus("Industry added successfully");
    } catch (err) {
      setStatus(err?.response?.data?.message || "Failed to add industry");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString();
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  };

  return (
    <Container fluid className="page-section py-4">
      <Row className="mb-4">
        <Col md={12}>
          <h3 className="page-title">Manage Industries</h3>
          <p className="page-subtitle mb-0">
            Add industries to make them available when generating Gemini market reports.
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={8} lg={6}>
          <Card>
            <Card.Body>
              <Card.Title>Add new industry</Card.Title>
              <div className="card-divider" />
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="industryName">
                  <Form.Label>Industry name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Life Science"
                    value={form.name}
                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="industryDescription">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Optional description"
                    value={form.description}
                    onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                  />
                </Form.Group>
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : "Add industry"}
                </Button>
              </Form>
              {status && <p className="text-muted mt-3">{status}</p>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Existing industries</Card.Title>
              <div className="card-divider" />
              {loading ? (
                <div className="py-4 text-center">
                  <LoadingBox />
                </div>
              ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
              ) : industries.length === 0 ? (
                <p className="text-muted mb-0">No industries found.</p>
              ) : (
                <Table responsive hover className="mt-3">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Slug</th>
                      <th>Created</th>
                      <th>Latest report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {industries.map((industry) => (
                      <tr key={industry._id || industry.slug}>
                        <td>{industry.name}</td>
                        <td>{industry.slug}</td>
                        <td>{formatDate(industry.createdAt)}</td>
                        <td>
                          {industry.latestReportSnapshot ? (
                            <div>
                              <div className="fw-semibold">
                                {industry.latestReportSnapshot.title}
                              </div>
                              <small className="text-muted">
                                {formatDateTime(industry.latestReportSnapshot.generatedAt)}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">No reports yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
